import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

/** Longest remembered list state kept; a longer one is simply not remembered. */
export const LAST_VIEW_MAX = 16_000;

/**
 * Per-user preferences (RELAY-937), kept in localStorage under the user's id so two people
 * sharing a browser do not share them. `pagination` is the ticket's "pagination as a
 * per-user setting": RecordTable reads it to choose infinite scroll or pages.
 * Theme is deliberately per DEVICE (`crm.theme`), not per user: it applies on the sign-in
 * page before anyone is known.
 */
export const settingsSchema = z.object({
  pagination: z.enum(['infinite', 'paged']).default('infinite'),
  pageSize: z.union([z.literal(50), z.literal(100), z.literal(200)]).default(100),
  /** Sidebar sections (`NavGroup`) folded to their label. */
  collapsedNavGroups: z.array(z.string()).default([]).catch([]),
  /** Multi board lanes folded to their header, by pipeline id. */
  collapsedBoardLanes: z.array(z.string()).default([]).catch([]),
  /** Each list's last encoded `TableState`, keyed `<organisationId>:<list>` (`useListView`). */
  lastViews: z.record(z.string(), z.string().max(LAST_VIEW_MAX)).default({}).catch({}),
});
export type Settings = z.infer<typeof settingsSchema>;
export const DEFAULT_SETTINGS: Settings = settingsSchema.parse({});

export const THEMES = ['system', 'light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];
const THEME_KEY = 'crm.theme';

const key = (userId: string) => `crm.settings.${userId}`;

function storedSettings(userId: string): Settings | null {
  try {
    const raw = localStorage.getItem(key(userId));
    const parsed = settingsSchema.safeParse(raw ? JSON.parse(raw) : {});
    return parsed.success ? parsed.data : null;
  } catch (err) {
    console.warn('settings read failed', err);
    return null;
  }
}

export function readSettings(userId: string): Settings {
  return storedSettings(userId) ?? DEFAULT_SETTINGS;
}

export function writeSettings(userId: string, settings: Settings): boolean {
  try {
    localStorage.setItem(key(userId), JSON.stringify(settings));
    return true;
  } catch (err) {
    console.warn('settings write failed', err);
    return false;
  }
}

export function useSettings(userId: string) {
  const [settings, setSettings] = useState<Settings>(() => readSettings(userId));
  const latest = useRef(settings);
  // After a failed write storage is stale, so `latest` holds the truth until a write succeeds.
  const writeFailed = useRef(false);
  useEffect(() => {
    writeFailed.current = false;
    latest.current = readSettings(userId);
    setSettings(latest.current);
  }, [userId]);
  const update = useCallback(
    (patch: Partial<Settings>) => {
      // Storage first: another `useSettings` (Settings page, shell) may have written since.
      const stored = writeFailed.current ? null : storedSettings(userId);
      const next = { ...(stored ?? latest.current), ...patch };
      writeFailed.current = !writeSettings(userId, next);
      latest.current = next;
      setSettings(next);
    },
    [userId],
  );
  return useMemo(() => [settings, update] as const, [settings, update]);
}

/**
 * What a device that never chose shows: light (TS-1600), not the OS setting — the product is
 * designed light-first. Anyone can still pick Dark or Match system, and that choice is kept.
 */
export const DEFAULT_THEME: Theme = 'light';

export function readTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    return THEMES.includes(raw as Theme) ? (raw as Theme) : DEFAULT_THEME;
  } catch (err) {
    console.warn('theme read failed', err);
    return DEFAULT_THEME;
  }
}

/** Puts `.dark` on <html> (the Tailwind `dark:` variant) for the chosen theme. */
export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement) {
  const prefersDark =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = theme === 'dark' || (theme === 'system' && prefersDark);
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readTheme);
  useEffect(() => applyTheme(theme), [theme]);
  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch (err) {
      console.warn('theme write failed', err);
    }
    setThemeState(next);
  }, []);
  return [theme, setTheme] as const;
}
